import { environment } from "@app/relay/relay-environment";
import JobCard, { type QueueJob } from "@app/components/account-queue/job-card";
import {
  accountQueueQuery,
  cancelMutation,
  enqueueMutation,
} from "@app/components/account-queue/queue-fragment";
import QueueSchedules from "@app/components/account-queue/queue-schedules";
import { Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { commitMutation, fetchQuery, useLazyLoadQuery } from "react-relay";

import type { queueFragment_Query } from "@app/queries/queueFragment_Query.graphql";
import type { queueFragment_EnqueueMutation } from "@app/queries/queueFragment_EnqueueMutation.graphql";
import type { queueFragment_CancelMutation } from "@app/queries/queueFragment_CancelMutation.graphql";

const POLL_MS = 2000;

// Marks the Stop/Retry buttons on a job card as the thing in flight. The start
// buttons key off their own kind, so this only has to be a value none of them
// can take.
const JOB_ACTION = "__job__";

// A child is only nested when its parent is on screen. A cascade whose parent
// has already finished is still a top-level row here — otherwise it would
// vanish from the in-flight list entirely.
function parentInList(job: QueueJob, list: ReadonlyArray<QueueJob>): boolean {
  return job.parentJobId != null && list.some((j) => j.jobId === job.parentJobId);
}

// What a person can ask for comes from the SERVER now (Jobs::Registry's
// `startable`). This file used to carry its own copy of that list, which meant
// adding a job kind meant editing a React file before anyone could reach it.

// Reads the queue itself rather than taking a preloaded ref from the route
// loader. The loader only runs on a client-side navigation here, so arriving by
// bookmark, refresh or a post-login redirect left the page blank — and the
// preloaded shape also needed a mirror of the data in state, plus an effect to
// resync it, before a poll could update anything. This is the same hook the
// footer has always used, and it works the same on every kind of arrival.
export default function QueuePage() {
  const [tick, setTick] = useState(0);
  // WHICH button is working, not merely that one is. The pressed button keeps a
  // spinner and its label; the others fade. A single boolean could not tell them
  // apart, so every button greyed out at once and nothing said which had been
  // pressed.
  const [pending, setPending] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Refetching inside a transition is what stops the page from blinking. A
  // fetchKey bump makes useLazyLoadQuery suspend, and with the only boundary up
  // at the route the whole page — heading, copy, buttons and all — dropped to a
  // loading state every two seconds while a job ran. In a transition React keeps
  // the rendered page on screen and swaps it when the data lands, so only the
  // job list changes.
  const [refreshing, startTransition] = useTransition();

  const data = useLazyLoadQuery<queueFragment_Query>(
    accountQueueQuery,
    { history: 50 },
    // A fetchKey bump does nothing under store-or-network — the store answers and
    // the poll never reaches the server. network-only is what makes this move.
    {
      fetchKey: tick,
      fetchPolicy: tick > 0 ? "network-only" : "store-or-network",
    }
  );

  // Fetch first, then bump the key. Bumping blindly makes useLazyLoadQuery do the
  // fetch, and a fetch that fails there THROWS on the next render; fetching here
  // means a dropped poll is a no-op that leaves the last good queue on screen.
  const refresh = useCallback(
    () =>
      fetchQuery<queueFragment_Query>(
        environment,
        accountQueueQuery,
        { history: 50 },
        { fetchPolicy: "network-only" }
      )
        .toPromise()
        .then(() => startTransition(() => setTick((n) => n + 1)))
        .catch(() => {}),
    []
  );

  const queue = data.accountQueue;
  const active = queue.active as ReadonlyArray<QueueJob>;
  const history = queue.history as ReadonlyArray<QueueJob>;
  const polling = queue.activeCount > 0;

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!polling) return;
    timer.current = setInterval(() => void refresh(), POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [polling, refresh]);

  const enqueue = (
    kind: string,
    params: Record<string, unknown>,
    retryJobId?: number
  ) => {
    setPending(kind || JOB_ACTION);
    setNotice(null);
    commitMutation<queueFragment_EnqueueMutation>(environment, {
      mutation: enqueueMutation,
      variables: { kind, params, retryJobId: retryJobId ?? null },
      onCompleted: (response, errors) => {
        const result = response.accountJobEnqueue;
        const failure = (errors ?? result?.errors)?.[0]?.message;
        if (failure) setNotice(failure);
        else if (result?.outcome === "deduped")
          setNotice(
            "That is already on the queue — showing you the one that is running."
          );
        else if (result?.outcome === "superseded")
          setNotice(
            `Queued. ${result.superseded.length} earlier job${
              result.superseded.length === 1 ? " was" : "s were"
            } cancelled because this replaces them.`
          );
        // Held until the list has the new job in it. Clearing on the mutation
        // alone would re-enable the buttons a beat before anything appeared,
        // which reads as if the click did nothing.
        void refresh().finally(() => setPending(null));
      },
      onError: () => {
        setPending(null);
        setNotice("Could not queue that. Please try again.");
      },
    });
  };

  const stop = (jobId: number) => {
    setPending(JOB_ACTION);
    commitMutation<queueFragment_CancelMutation>(environment, {
      mutation: cancelMutation,
      variables: { jobId },
      onCompleted: (response) => {
        setNotice(
          response.accountJobCancel?.outcome === "stopping"
            ? "Asked it to stop — it will finish the step it is on. Press Stop again to give up waiting."
            : "Stopped."
        );
        void refresh().finally(() => setPending(null));
      },
      onError: () => setPending(null),
    });
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <h1 className="text-xl font-bold">Queue</h1>

      <div className="max-w-3xl space-y-2 text-sm text-gray-500">
        <p>
          Everything that touches this account runs here, one at a time.
          Imports, syncs, merges and metrics rebuilds all contend for the same
          account, so putting them in one queue is what stops them from
          colliding — and lets each one say what it is waiting for.
        </p>
        <p>
          Finished jobs are never deleted. This is the record of every action
          taken on the account: who asked, when, what it produced, and why it
          stopped.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {queue.startableKinds.map((option) => {
          const already = queue.queuedKinds.includes(option.kind);
          const working = pending === option.kind;
          const disabled = pending !== null || already;

          return (
            <button
              key={option.kind}
              type="button"
              disabled={disabled}
              aria-busy={working}
              onClick={() =>
                enqueue(option.kind, (option.params ?? {}) as Record<string, unknown>)
              }
              title={
                already
                  ? "Already on the queue — queueing it again would do the same work twice"
                  : queue.activeCount > 0
                  ? "Something is running. This will queue behind it and start when that finishes."
                  : undefined
              }
              // Opacity is computed rather than left to `disabled:opacity-40`,
              // because the pressed button is disabled too and must NOT fade —
              // it is the one carrying the spinner, and a faded spinner reads
              // as "broken" rather than "working".
              className={[
                "inline-flex items-center gap-1.5 rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-700",
                working
                  ? "opacity-100"
                  : disabled
                  ? "opacity-40"
                  : "hover:bg-gray-50",
              ].join(" ")}
            >
              {working && <Loader2 size={14} className="animate-spin" />}
              {working
                ? "Queueing…"
                : already
                ? `${option.label} — already queued`
                : option.label}
            </button>
          );
        })}

        {/* Deliberately not a blocker. The queue takes work while something is
            running — Jobs::Enqueue queues it behind and dedupes an identical
            request rather than failing — so disabling everything during a run
            would remove the thing the queue is for. The only case worth
            stopping is asking twice for the same kind, which the per-kind
            guard above already covers. */}
        {refreshing && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
            <Loader2 size={12} className="animate-spin" />
            updating
          </span>
        )}
      </div>

      {notice && (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
          {notice}
        </div>
      )}

      {active.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">
            In flight — {active.length} job{active.length === 1 ? "" : "s"}
          </h2>
          {/* Children sit UNDER the job that caused them. A flat list cannot
              answer "why did a metrics rebuild start on its own?", which is the
              single most confusing thing a cascade does — the answer is one row
              above it, and now it looks like it. */}
          {active
            .filter((job) => !parentInList(job, active))
            .map((job) => (
              <div key={job.jobId} className="space-y-2">
                <JobCard
                  job={job}
                  busy={pending !== null}
                  onStop={stop}
                  onRetry={(id) => enqueue("", {}, id)}
                />
                {active
                  .filter((child) => child.parentJobId === job.jobId)
                  .map((child) => (
                    <div
                      key={child.jobId}
                      className="ml-6 border-l-2 border-gray-200 pl-4"
                    >
                      <p className="mb-1 text-xs text-gray-400">
                        queued by job #{job.jobId}
                      </p>
                      <JobCard
                        job={child}
                        busy={pending !== null}
                        onStop={stop}
                        onRetry={(id) => enqueue("", {}, id)}
                      />
                    </div>
                  ))}
              </div>
            ))}
        </div>
      )}

      <QueueSchedules
        schedules={queue.schedules}
        startable={queue.startableKinds}
        busy={pending !== null}
        onChanged={() => void refresh()}
        onNotice={setNotice}
      />

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">History</h2>
        {history.length === 0 ? (
          <div className="rounded border border-dashed border-gray-300 p-6 text-sm text-gray-500">
            Nothing has run on this account yet.
          </div>
        ) : (
          history.map((job) => (
            <div key={job.jobId} className="space-y-1">
              <JobCard
                job={job}
                busy={pending !== null}
                onStop={stop}
                onRetry={(id) => enqueue("", {}, id)}
              />
              <Link
                to="/queue/$jobId"
                params={{ jobId: String(job.jobId) }}
                className="ml-1 text-xs text-blue-600 hover:underline"
              >
                Open job #{job.jobId}
              </Link>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
